import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { corporateTheme } from '../themes/corporate';

const BusinessContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
`;

const Title = styled(motion.h2)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.textPrimary};
  text-align: center;
  margin-bottom: 3rem;
  background: ${corporateTheme.primaryGradient};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-family: ${corporateTheme.fontFamily};
`;

const ModelGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  margin-bottom: 4rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ModelSection = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(10px);
  border-radius: ${corporateTheme.borderRadius.xl};
  padding: 3rem;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
`;

const SectionTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  color: ${corporateTheme.primary};
  margin-bottom: 2rem;
  text-align: center;
  font-family: ${corporateTheme.fontFamily};
`;

const ServiceCard = styled(motion.div)`
  background: ${corporateTheme.background};
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 1.5rem;
  border: 1px solid ${corporateTheme.cardBorder};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${corporateTheme.backgroundSecondary};
    border-color: ${corporateTheme.primary};
    box-shadow: ${corporateTheme.cardShadow};
  }
`;

const ServiceTitle = styled.h4`
  font-size: 1.3rem;
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const ServiceDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  line-height: 1.6;
  font-size: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const RevenueStreams = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
`;

const RevenueCard = styled(motion.div)`
  background: ${corporateTheme.cardBackground};
  backdrop-filter: blur(5px);
  border-radius: ${corporateTheme.borderRadius.lg};
  padding: 2rem;
  text-align: center;
  border: 1px solid ${corporateTheme.cardBorder};
  box-shadow: ${corporateTheme.cardShadow};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: ${corporateTheme.primaryGradient};
  }
`;

const RevenueIcon = styled.div`
  font-size: 0.9rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.background};
  background: ${corporateTheme.primaryGradient};
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin: 0 auto 1rem;
  font-family: ${corporateTheme.fontFamily};
  letter-spacing: 0.5px;
`;

const RevenueTitle = styled.h4`
  font-size: 1.2rem;
  color: ${corporateTheme.textPrimary};
  margin-bottom: 0.5rem;
  font-weight: ${corporateTheme.fontWeights.semibold};
  font-family: ${corporateTheme.fontFamily};
`;

const RevenuePercentage = styled.div`
  font-size: 2rem;
  font-weight: ${corporateTheme.fontWeights.bold};
  color: ${corporateTheme.primary};
  margin-bottom: 1rem;
  font-family: ${corporateTheme.fontFamily};
`;

const RevenueDescription = styled.p`
  color: ${corporateTheme.textSecondary};
  font-size: 0.9rem;
  line-height: 1.5;
  font-family: ${corporateTheme.fontFamily};
`;

const BusinessModel = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const advertiserServices = [
    {
      title: "Affiliate Network Platform",
      description: "Robust, user-friendly platform for campaign management, tracking, reporting, and payment processing with real-time analytics."
    },
    {
      title: "Proprietary Traffic Access",
      description: "Direct access to highly qualified traffic from our network of internally developed, content-rich websites and digital assets."
    },
    {
      title: "Performance Tracking & Analytics",
      description: "Real-time, granular data on clicks, conversions, sales, and ROI with advanced analytics to identify trends and opportunities."
    },
    {
      title: "Fraud Detection & Prevention",
      description: "Sophisticated tools and manual reviews to ensure clean traffic and legitimate conversions from all sources."
    }
  ];

  const affiliateServices = [
    {
      title: "Diverse Campaign Offers",
      description: "Access to a wide range of relevant and high-converting campaigns from reputable advertisers across various verticals."
    },
    {
      title: "Performance Tracking & Reporting",
      description: "Real-time access to performance data, earnings, and payment status with comprehensive analytics dashboard."
    },
    {
      title: "Prompt & Reliable Payments",
      description: "Regular and timely payouts via preferred local payment methods across Eastern Asian markets."
    },
    {
      title: "Educational Resources",
      description: "Best practices, industry insights, and training to help affiliates optimize their performance and grow their business."
    }
  ];

  const revenueStreams = [
    {
      icon: "REV",
      title: "Commission Revenue",
      percentage: "70%",
      description: "Primary revenue from affiliate commissions and advertiser fees"
    },
    {
      icon: "SETUP",
      title: "Setup Fees",
      percentage: "15%",
      description: "One-time setup fees for new advertisers and premium features"
    },
    {
      icon: "SUB",
      title: "Subscription Fees",
      percentage: "10%",
      description: "Tiered subscription for premium features and dedicated account management"
    },
    {
      icon: "CONS",
      title: "Consulting Services",
      percentage: "5%",
      description: "Strategic advice and content optimization consulting"
    }
  ];

  return (
    <BusinessContainer ref={ref}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <Title variants={itemVariants}>
          Business Model
        </Title>

        <ModelGrid>
          <ModelSection variants={itemVariants}>
            <SectionTitle>For Advertisers</SectionTitle>
            {advertiserServices.map((service, index) => (
              <ServiceCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -2 }}
                transition={{ duration: 0.3 }}
              >
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ModelSection>

          <ModelSection variants={itemVariants}>
            <SectionTitle>For Affiliates</SectionTitle>
            {affiliateServices.map((service, index) => (
              <ServiceCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.02, y: -2 }}
                transition={{ duration: 0.3 }}
              >
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ModelSection>
        </ModelGrid>

        <motion.div variants={itemVariants}>
          <SectionTitle style={{ textAlign: 'center', marginBottom: '2rem' }}>
            Revenue Streams
          </SectionTitle>
          <RevenueStreams>
            {revenueStreams.map((stream, index) => (
              <RevenueCard
                key={index}
                variants={itemVariants}
                whileHover={{ scale: 1.05, y: -5 }}
                transition={{ duration: 0.3 }}
              >
                <RevenueIcon>{stream.icon}</RevenueIcon>
                <RevenueTitle>{stream.title}</RevenueTitle>
                <RevenuePercentage>{stream.percentage}</RevenuePercentage>
                <RevenueDescription>{stream.description}</RevenueDescription>
              </RevenueCard>
            ))}
          </RevenueStreams>
        </motion.div>
      </motion.div>
    </BusinessContainer>
  );
};

export default BusinessModel; 